import { z } from 'zod'
import { createEnv } from '@t3-oss/env-nextjs'

export const env = (() => {
  /**
   * Override the APP_BASE_URL env var with the CUSTOM_URL env var if it exists.
   * Otherwise, if the APP_BASE_URL env var is not set, default to the VERCEL_URL env var.
   */

  const originalAuth0BaseUrl = process.env.APP_BASE_URL

  const newAuth0BaseUrl =
    process.env.CUSTOM_URL ??
    process.env.APP_BASE_URL ??
    (process.env.VERCEL_URL ? `https://${process.env.VERCEL_URL}` : undefined)

  if (originalAuth0BaseUrl !== newAuth0BaseUrl) {
    process.env.APP_BASE_URL = newAuth0BaseUrl

    // biome-ignore lint/suspicious/noConsole: can not user logger in env.js
    console.log('Modified environment:', {
      originalAuth0BaseUrl,
      newAuth0BaseUrl,
      'process.env.APP_BASE_URL': process.env.APP_BASE_URL,
      'process.env.CUSTOM_URL': process.env.CUSTOM_URL,
      'process.env.VERCEL_URL': process.env.VERCEL_URL,
    })
  }

  process.env.BASE_URL = process.env.BASE_URL ?? process.env.APP_BASE_URL

  /**
   * @param {`d-${string}`} id - The ID of the email template.
   */
  const EmailTemplateSchema = (id) => z.string().min(1).catch(id)

  const result = createEnv({
    /**
     * Specify your server-side environment variables schema here. This way you can ensure the app isn't
     * built with invalid env vars.
     */
    server: {
      ABLY_API_KEY: z.string().min(1),
      ANTHROPIC_API_KEY: z.string().min(1),
      APP_BASE_URL: z.url(),
      AUTH_BRIDGE_DEBUG: z.stringbool().optional(),
      AUTH_DEBUG: z.stringbool().optional(),
      BASE_URL: z.url(),
      CHARGE_GATEWAY_PROVIDER: z.string().optional(),
      CLERK_SECRET_KEY: z.string(),
      CLOUDINARY_API_BASE_URL: z.url(),
      CLOUDINARY_API_KEY: z.string().min(1),
      CLOUDINARY_API_SECRET: z.string().min(1),
      CLOUDINARY_CLOUD_NAME: z.string().min(1),
      CUSTOM_URL: z.url().optional(),
      DATABASE_URL: z.url(),
      DATADOG_SERVICE: z.string().optional(),
      DB_DEBUG: z.stringbool().optional(),
      DEBUG: z.string().optional(),
      DISABLE_ALL_NOTIFICATIONS: z.enum(['true', 'false']).optional(),
      DISABLE_ASYNC_MESSAGES: z.enum(['true', 'false']).optional(),
      DISABLE_EMAIL_NOTIFICATIONS: z.enum(['true', 'false']).optional(),
      EMAIL_TEMPLATE_ALL_NEW_USER_CREATED: EmailTemplateSchema(
        'd-dca20629e56447b5bc42be90cd139394'
      ),
      EMAIL_TEMPLATE_ORGANIZATION_ONBOARDING_COMPLETE: EmailTemplateSchema(
        'd-1f76e063e7434ad0b30a78dd56807ee9'
      ),
      EMAIL_TEMPLATE_ORGANIZATION_ONBOARDING_FAILED_INTERVIEW:
        EmailTemplateSchema('d-8d15b67b47794c289cc4fef95d77b9cd'),
      EMAIL_TEMPLATE_QUICKFRAME_ACTIVITY_NOTIFICATION: EmailTemplateSchema(
        'd-65710be42682406c8e84be4261744f1d'
      ),
      EXIF_QF_CONFIG_PATH: z
        .string()
        .min(1)
        .catch('/opt/tagger/qf-xmp-custom-namespace.pl'),
      EXIF_TOOL_PATH: z.string().min(1).catch('/opt/bin/exiftool'),
      FILE_URL_WINDOW_SECONDS: z.coerce.number().positive().catch(60),
      INNGEST_ALERT_FAILURES: z.stringbool(),
      INNGEST_API_URL: z.url().catch('https://api.inngest.com/v1'),
      INNGEST_APP_URL: z.string().optional(),
      INNGEST_SIGNING_KEY: z.string().min(1),
      INNGEST_SLACK_CHANNEL: z.string(),
      INNGEST_SLACK_TOKEN: z.string(),
      INTERCOM_TOKEN: z.string(),
      LAUNCHDARKLY_DEV_SERVER_URL: z.url().optional(),
      LAUNCHDARKLY_SDK_KEY: z.string().min(1),
      META_TAGGER_URL: z.url(),
      MNTN_COST_MARGIN_PERCENT: z.coerce
        .number()
        .int()
        .nonnegative()
        .max(99)
        .optional(),
      MNTN_FINANCE_MANAGER_PROVIDER: z.enum([
        'unified-billing-service',
        'mock',
      ]),
      MNTN_PROXY_URL: z.url(),
      MNTN_CLIENT_ID: z.string(),
      MNTN_CLIENT_SECRET: z.string(),
      NODE_ENV: z.enum(['development', 'production', 'test']).optional(),
      OPENAI_API_KEY: z.string().min(1),
      OTEL_LOG_LEVEL: z.enum(['error', 'warn', 'info', 'debug']).optional(),
      PENDO_API_KEY: z.string().min(1),
      PRINCIPAL_SESSION_KEY: z.string(),
      SPLIT_SERVER_KEY: z.string(),
      SENDGRID_API_KEY: z.string().min(1),
      SENDGRID_VERIFIED_SENDER_EMAIL: z.email(),
      SENDGRID_VERIFIED_SENDER_NAME: z.string(),
      STRIPE_SECRET_KEY: z.string().min(1),
      TIPALTI_MASTER_KEY: z.string().min(1),
      TIPALTI_PAYER_NAME: z.string().min(1),
      TIPALTI_SETUP_IFRAME_URL: z.url(),
      TIPALTI_IPN_VERIFY: z.url(),
      UBS_BASE_PATH: z.string().optional(),
      UBS_CREDIT_BANK_BASE_PATH: z.string().optional(),
      UBS_CREDIT_BANK_JWT_ISS: z.string().optional(),
      UBS_CREDIT_BANK_JWT_SECRET: z.string().optional(),
      UBS_CREDIT_BANK_JWT_SUB: z.string().optional(),
      UBS_CREDIT_BANK_JWT_TYPE: z.string().optional(),
      UBS_CREDIT_BANK_URL: z.url().optional(),
      UBS_JWT_NAME: z.string().optional(),
      UBS_JWT_ROLE: z.string().optional(),
      UBS_JWT_SECRET: z.string().optional(),
      UBS_JWT_SUB: z.string().optional(),
      UBS_URL: z.url().optional(),
      VERCEL: z.string().optional(),
      VERCEL_ENV: z.enum(['production', 'preview', 'development']).optional(),
      VERCEL_URL: z.string().optional(),
      WORKRAMP_SIGNING_SECRET: z.string(),
    },
    /**
     * Specify your client-side environment variables schema here.
     * For them to be exposed to the client, prefix them with `NEXT_PUBLIC_`.
     */
    client: {
      NEXT_PUBLIC_AUTH_BASE_URL: z.url(),
      NEXT_PUBLIC_AUTH_DEBUG: z.stringbool().optional(),
      NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY: z.string(),
      NEXT_PUBLIC_CLERK_SIGN_IN_URL: z.url(),
      NEXT_PUBLIC_CLOUDINARY_API_KEY: z.string().min(1),
      NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME: z.string().min(1),
      NEXT_PUBLIC_FORCE_AUTH_MODE: z.enum(['mntn', 'clerk']).optional(),
      NEXT_PUBLIC_INTERCOM_APP_ID: z.string().min(1),
      NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_ID: z.string().min(1),
      NEXT_PUBLIC_LAUNCHDARKLY_DEV_SERVER_URL: z.url().optional(),
      NEXT_PUBLIC_LOG_LEVEL: z
        .enum(['error', 'warn', 'info', 'debug'])
        .catch('error'),
      NEXT_PUBLIC_ONBOARDING_CERTIFICATION_URL: z.url(),
      NEXT_PUBLIC_ONBOARDING_INTERVIEW_URL: z.url(),
      NEXT_PUBLIC_PASS_BASE_URL: z.url(),
      NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: z.string().min(1),
      NEXT_PUBLIC_DATADOG_CLIENT_TOKEN: z.string().min(1).optional(),
      NEXT_PUBLIC_DATADOG_APPLICATION_ID: z.string().min(1).optional(),
      NEXT_PUBLIC_DATADOG_SITE: z.string().optional(),
      NEXT_PUBLIC_DATADOG_SERVICE: z.string().optional(),
      NEXT_PUBLIC_DATADOG_ENV: z.string().optional(),
      NEXT_PUBLIC_DATADOG_VERSION: z.string().optional(),
      NEXT_PUBLIC_DATADOG_SESSION_SAMPLE_RATE: z.string().optional(),
      NEXT_PUBLIC_DATADOG_SESSION_REPLAY_SAMPLE_RATE: z.string().optional(),
      NEXT_PUBLIC_DATADOG_TRACE_SAMPLE_RATE: z.string().optional(),
      NEXT_PUBLIC_DATADOG_ALLOWED_TRACING_URLS: z.string().optional(),
    },
    /**
     * Destructure all variables from `process.env` to make sure they aren't tree-shaken away.
     */
    runtimeEnv: {
      ABLY_API_KEY: process.env.ABLY_API_KEY,
      ANTHROPIC_API_KEY: process.env.ANTHROPIC_API_KEY,
      APP_BASE_URL: process.env.APP_BASE_URL,
      AUTH_BRIDGE_DEBUG: process.env.AUTH_BRIDGE_DEBUG,
      AUTH_DEBUG: process.env.AUTH_DEBUG,
      NEXT_PUBLIC_AUTH_BASE_URL: process.env.NEXT_PUBLIC_AUTH_BASE_URL,
      NEXT_PUBLIC_AUTH_DEBUG: process.env.NEXT_PUBLIC_AUTH_DEBUG,
      BASE_URL: process.env.BASE_URL,
      CHARGE_GATEWAY_PROVIDER: process.env.CHARGE_GATEWAY_PROVIDER,
      CLERK_SECRET_KEY: process.env.CLERK_SECRET_KEY,
      CLOUDINARY_API_BASE_URL: process.env.CLOUDINARY_API_BASE_URL,
      CLOUDINARY_API_KEY: process.env.CLOUDINARY_API_KEY,
      CLOUDINARY_API_SECRET: process.env.CLOUDINARY_API_SECRET,
      CLOUDINARY_CLOUD_NAME: process.env.CLOUDINARY_CLOUD_NAME,
      CUSTOM_URL: process.env.CUSTOM_URL,
      DATABASE_URL: process.env.DATABASE_URL,
      DATADOG_SERVICE: process.env.DATADOG_SERVICE,
      DB_DEBUG: process.env.DB_DEBUG,
      DEBUG: process.env.DEBUG,
      DISABLE_ALL_NOTIFICATIONS: process.env.DISABLE_ALL_NOTIFICATIONS,
      DISABLE_ASYNC_MESSAGES: process.env.DISABLE_ASYNC_MESSAGES,
      DISABLE_EMAIL_NOTIFICATIONS: process.env.DISABLE_EMAIL_NOTIFICATIONS,
      EMAIL_TEMPLATE_ALL_NEW_USER_CREATED:
        process.env.EMAIL_TEMPLATE_ALL_NEW_USER_CREATED,
      EMAIL_TEMPLATE_ORGANIZATION_ONBOARDING_COMPLETE:
        process.env.EMAIL_TEMPLATE_ORGANIZATION_ONBOARDING_COMPLETE,
      EMAIL_TEMPLATE_ORGANIZATION_ONBOARDING_FAILED_INTERVIEW:
        process.env.EMAIL_TEMPLATE_ORGANIZATION_ONBOARDING_FAILED_INTERVIEW,
      EMAIL_TEMPLATE_QUICKFRAME_ACTIVITY_NOTIFICATION:
        process.env.EMAIL_TEMPLATE_QUICKFRAME_ACTIVITY_NOTIFICATION,
      EXIF_QF_CONFIG_PATH: process.env.EXIF_QF_CONFIG_PATH,
      EXIF_TOOL_PATH: process.env.EXIF_TOOL_PATH,
      FILE_URL_WINDOW_SECONDS: process.env.FILE_URL_WINDOW_SECONDS,
      INNGEST_ALERT_FAILURES: process.env.INNGEST_ALERT_FAILURES,
      INNGEST_API_URL: process.env.INNGEST_API_URL,
      INNGEST_APP_URL: process.env.INNGEST_APP_URL,
      INNGEST_SIGNING_KEY: process.env.INNGEST_SIGNING_KEY,
      INNGEST_SLACK_CHANNEL: process.env.INNGEST_SLACK_CHANNEL,
      INNGEST_SLACK_TOKEN: process.env.INNGEST_SLACK_TOKEN,
      INTERCOM_TOKEN: process.env.INTERCOM_TOKEN,
      LAUNCHDARKLY_SDK_KEY: process.env.LAUNCHDARKLY_SDK_KEY,
      LAUNCHDARKLY_DEV_SERVER_URL: process.env.LAUNCHDARKLY_DEV_SERVER_URL,
      META_TAGGER_URL: process.env.META_TAGGER_URL,
      MNTN_COST_MARGIN_PERCENT: process.env.MNTN_COST_MARGIN_PERCENT,
      MNTN_FINANCE_MANAGER_PROVIDER: process.env.MNTN_FINANCE_MANAGER_PROVIDER,
      MNTN_PROXY_URL: process.env.MNTN_PROXY_URL,
      MNTN_CLIENT_ID: process.env.MNTN_CLIENT_ID,
      MNTN_CLIENT_SECRET: process.env.MNTN_CLIENT_SECRET,
      NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY:
        process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY,
      NEXT_PUBLIC_CLERK_SIGN_IN_URL: process.env.NEXT_PUBLIC_CLERK_SIGN_IN_URL,
      NEXT_PUBLIC_CLOUDINARY_API_KEY:
        process.env.NEXT_PUBLIC_CLOUDINARY_API_KEY,
      NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME:
        process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME,
      NEXT_PUBLIC_FORCE_AUTH_MODE: process.env.NEXT_PUBLIC_FORCE_AUTH_MODE,
      NEXT_PUBLIC_INTERCOM_APP_ID: process.env.NEXT_PUBLIC_INTERCOM_APP_ID,
      NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_ID:
        process.env.NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_ID,
      NEXT_PUBLIC_LAUNCHDARKLY_DEV_SERVER_URL:
        process.env.LAUNCHDARKLY_DEV_SERVER_URL,
      NEXT_PUBLIC_LOG_LEVEL: process.env.NEXT_PUBLIC_LOG_LEVEL,
      NEXT_PUBLIC_ONBOARDING_CERTIFICATION_URL:
        process.env.NEXT_PUBLIC_ONBOARDING_CERTIFICATION_URL,
      NEXT_PUBLIC_ONBOARDING_INTERVIEW_URL:
        process.env.NEXT_PUBLIC_ONBOARDING_INTERVIEW_URL,
      NEXT_PUBLIC_PASS_BASE_URL: process.env.NEXT_PUBLIC_PASS_BASE_URL,
      NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY:
        process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY,
      NEXT_PUBLIC_DATADOG_CLIENT_TOKEN:
        process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
      NEXT_PUBLIC_DATADOG_APPLICATION_ID:
        process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID,
      NEXT_PUBLIC_DATADOG_SITE: process.env.NEXT_PUBLIC_DATADOG_SITE,
      NEXT_PUBLIC_DATADOG_SERVICE: process.env.NEXT_PUBLIC_DATADOG_SERVICE,
      NEXT_PUBLIC_DATADOG_ENV: process.env.NEXT_PUBLIC_DATADOG_ENV,
      NEXT_PUBLIC_DATADOG_VERSION: process.env.NEXT_PUBLIC_DATADOG_VERSION,
      NEXT_PUBLIC_DATADOG_SESSION_SAMPLE_RATE:
        process.env.NEXT_PUBLIC_DATADOG_SESSION_SAMPLE_RATE,
      NEXT_PUBLIC_DATADOG_SESSION_REPLAY_SAMPLE_RATE:
        process.env.NEXT_PUBLIC_DATADOG_SESSION_REPLAY_SAMPLE_RATE,
      NEXT_PUBLIC_DATADOG_TRACE_SAMPLE_RATE:
        process.env.NEXT_PUBLIC_DATADOG_TRACE_SAMPLE_RATE,
      NEXT_PUBLIC_DATADOG_ALLOWED_TRACING_URLS:
        process.env.NEXT_PUBLIC_DATADOG_ALLOWED_TRACING_URLS,
      NODE_ENV: process.env.NODE_ENV,
      OPENAI_API_KEY: process.env.OPENAI_API_KEY,
      OTEL_LOG_LEVEL: process.env.OTEL_LOG_LEVEL,
      PENDO_API_KEY: process.env.PENDO_API_KEY,
      PRINCIPAL_SESSION_KEY: process.env.PRINCIPAL_SESSION_KEY,
      SENDGRID_API_KEY: process.env.SENDGRID_API_KEY,
      SENDGRID_VERIFIED_SENDER_EMAIL:
        process.env.SENDGRID_VERIFIED_SENDER_EMAIL,
      SENDGRID_VERIFIED_SENDER_NAME: process.env.SENDGRID_VERIFIED_SENDER_NAME,
      SPLIT_SERVER_KEY: process.env.SPLIT_SERVER_KEY,
      STRIPE_SECRET_KEY: process.env.STRIPE_SECRET_KEY,
      TIPALTI_MASTER_KEY: process.env.TIPALTI_MASTER_KEY,
      TIPALTI_PAYER_NAME: process.env.TIPALTI_PAYER_NAME,
      TIPALTI_SETUP_IFRAME_URL: process.env.TIPALTI_SETUP_IFRAME_URL,
      TIPALTI_IPN_VERIFY: process.env.TIPALTI_IPN_VERIFY,
      UBS_BASE_PATH: process.env.UBS_BASE_PATH,
      UBS_CREDIT_BANK_BASE_PATH: process.env.UBS_CREDIT_BANK_BASE_PATH,
      UBS_CREDIT_BANK_JWT_ISS: process.env.UBS_CREDIT_BANK_JWT_ISS,
      UBS_CREDIT_BANK_JWT_SECRET: process.env.UBS_CREDIT_BANK_JWT_SECRET,
      UBS_CREDIT_BANK_JWT_SUB: process.env.UBS_CREDIT_BANK_JWT_SUB,
      UBS_CREDIT_BANK_JWT_TYPE: process.env.UBS_CREDIT_BANK_JWT_TYPE,
      UBS_CREDIT_BANK_URL: process.env.UBS_CREDIT_BANK_URL,
      UBS_JWT_NAME: process.env.UBS_JWT_NAME,
      UBS_JWT_ROLE: process.env.UBS_JWT_ROLE,
      UBS_JWT_SECRET: process.env.UBS_JWT_SECRET,
      UBS_JWT_SUB: process.env.UBS_JWT_SUB,
      UBS_URL: process.env.UBS_URL,
      VERCEL: process.env.VERCEL,
      VERCEL_ENV: process.env.VERCEL_ENV,
      VERCEL_URL: process.env.VERCEL_URL,
      WORKRAMP_SIGNING_SECRET: process.env.WORKRAMP_SIGNING_SECRET,
    },
    skipValidation:
      process.env.NODE_ENV === 'test' ||
      !!process.env.CI ||
      !!process.env.SKIP_ENV_VALIDATION,
  })

  return result
})()
